import { NEW_CATALOG_NAME } from "@app/config/const";
import StatusBarElement from "@components/Layouts/StatusBar/StatusBarElement";
import { useRouter } from "@core/Api/useRouter";
import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import type GitSourceData from "@ext/git/core/model/GitSourceData.schema";
import type GitStorageData from "@ext/git/core/model/GitStorageData";
import t from "@ext/localization/locale/translate";
import SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import { useCallback, useState } from "react";
import ApiUrlCreatorService from "../../../../ui-logic/ContextServices/ApiUrlCreator";

export const GesCloudConnectStorage = () => {
	const catalogPropsStore = useCatalogPropsStore((state) => state, "shallow");
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const sourceDatas = SourceDataService.value;
	const router = useRouter();

	const [isConnectingCatalog, setIsConnectingCatalog] = useState(false);

	const handleClick = useCallback(async () => {
		if (isConnectingCatalog) return;
		setIsConnectingCatalog(true);
		try {
			const oldCatalogName = catalogPropsStore.data.repositoryName;
			const title = catalogPropsStore.data.title || NEW_CATALOG_NAME;

			const gitlabSourceData = sourceDatas.find((sourceData) => sourceData.sourceType === SourceType.gitLab);
			const gesCloudApi = new GesCloudApi(gesCloudUrl);
			const initData = await gesCloudApi.getCatalogInitData(title);

			const data: GitStorageData = {
				source: gitlabSourceData as GitSourceData,
				name: initData.git.repositoryName,
				group: initData.git.group,
			};
			const res = await FetchService.fetch<ClientCatalogProps>(
				apiUrlCreator.getInitEnterpriseCloudCatalogUrl(oldCatalogName, initData.git.repositoryName),
				JSON.stringify(data),
				MimeTypes.json,
			);
			if (res.ok) {
				const newCatalogProps = await res.json();
				catalogPropsStore.update(newCatalogProps);
				router.pushPath(newCatalogProps.link.pathname);
			}
		} catch (error) {
			ErrorConfirmService.notify(error);
		} finally {
			setIsConnectingCatalog(false);
		}
	}, [isConnectingCatalog, catalogPropsStore, gesCloudUrl, router]);

	return (
		<StatusBarElement
			disable={isConnectingCatalog}
			iconClassName={isConnectingCatalog ? "animate-spin" : ""}
			iconCode={isConnectingCatalog ? "loader-circle" : "cloud-upload"}
			iconStyle={{ fontSize: "15px" }}
			onClick={handleClick}
			tooltipText={t("publish-catalog")}
		/>
	);
};
